/**
 * useMobileSwipe Hook for LOOPINO AI Mobile
 * Enhanced hook for fluid swipe gestures with real-time updates
 */

import { useState, useRef, useCallback, useEffect } from 'react';

export const useMobileSwipe = (onSwipeLeft, onSwipeRight, options = {}) => {
  const {
    threshold = 50,
    velocityThreshold = 0.3,
    touchStartDelay = 0,
    preventDefaultTouchmove = true,
    resistanceStiffness = 0.3,
    disabled = false,
    onSwipeStart,
    onSwipeEnd,
    onSwipeUpdate, // NEW: Real-time swipe updates
    returnAnimationDuration = 250,
    dampingFactor = 0.8 // NEW: For smoother momentum
  } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [currentX, setCurrentX] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [isHorizontalSwipe, setIsHorizontalSwipe] = useState(false);
  const [swipeVelocity, setSwipeVelocity] = useState(0);

  const touchStartTimer = useRef(null);
  const animationFrameRef = useRef(null);
  const lastUpdateTime = useRef(0);
  const velocityHistory = useRef([]);

  // NEW: Calculate velocity with smoothing
  const calculateVelocity = useCallback((currentTime, currentPosition, startPosition, startTime) => {
    const timeDelta = currentTime - startTime;
    const distance = currentPosition - startPosition;
    
    if (timeDelta === 0) return 0;
    
    const instantVelocity = Math.abs(distance) / timeDelta;
    
    // Add to velocity history for smoothing
    velocityHistory.current.push(instantVelocity);
    if (velocityHistory.current.length > 5) {
      velocityHistory.current.shift();
    }
    
    // Return average velocity for smoother calculation
    return velocityHistory.current.reduce((a, b) => a + b, 0) / velocityHistory.current.length;
  }, []);

  // Apply resistance when swiping beyond boundaries with improved curve
  const getResistantOffset = useCallback((offset, maxOffset = 300) => {
    if (Math.abs(offset) <= maxOffset * 0.7) return offset;
    
    const sign = offset < 0 ? -1 : 1;
    const normalizedOffset = Math.abs(offset) / maxOffset;
    
    // Use a more natural resistance curve
    const resistance = Math.pow(normalizedOffset, 1.5) * resistanceStiffness;
    const resistantOffset = maxOffset * 0.7 + (Math.abs(offset) - maxOffset * 0.7) * (1 - resistance);
    
    return sign * resistantOffset;
  }, [resistanceStiffness]);

  // NEW: Real-time swipe state update
  const updateSwipeState = useCallback(() => {
    if (!isDragging) return;
    
    const swipeState = {
      isDragging,
      swipeOffset: getResistantOffset(currentX - startX),
      rawSwipeOffset: currentX - startX,
      isHorizontalSwipe,
      swipeDirection: (currentX - startX) > 0 ? 'right' : 'left',
      swipeProgress: Math.abs(currentX - startX) / threshold,
      velocity: swipeVelocity
    };
    
    if (onSwipeUpdate) {
      onSwipeUpdate(swipeState);
    }
  }, [isDragging, currentX, startX, isHorizontalSwipe, threshold, swipeVelocity, onSwipeUpdate, getResistantOffset]);

  // NEW: Update swipe state on every frame while dragging
  useEffect(() => {
    if (isDragging) {
      const animate = () => {
        updateSwipeState();
        animationFrameRef.current = requestAnimationFrame(animate);
      };
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isDragging, updateSwipeState]);

  const handleTouchStart = useCallback((e) => {
    if (disabled) return;
    
    const touch = e.touches[0];
    const currentTime = Date.now();
    
    setStartX(touch.clientX);
    setStartY(touch.clientY);
    setCurrentX(touch.clientX);
    setCurrentY(touch.clientY);
    setStartTime(currentTime);
    setIsHorizontalSwipe(false);
    setSwipeVelocity(0);
    
    // Reset velocity history
    velocityHistory.current = [];
    lastUpdateTime.current = currentTime;
    
    // Call onSwipeStart callback
    if (onSwipeStart) {
      onSwipeStart();
    }
    
    if (touchStartDelay > 0) {
      touchStartTimer.current = setTimeout(() => {
        setIsDragging(true);
      }, touchStartDelay);
    } else {
      setIsDragging(true);
    }
  }, [touchStartDelay, disabled, onSwipeStart]);

  const handleTouchMove = useCallback((e) => {
    if (disabled || (!isDragging && touchStartDelay > 0)) return;
    
    const touch = e.touches[0];
    const currentTime = Date.now();
    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;
    
    // Determine if this is a horizontal swipe
    if (!isHorizontalSwipe && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
      const horizontal = Math.abs(deltaX) > Math.abs(deltaY);
      setIsHorizontalSwipe(horizontal);
      
      // Only prevent default for horizontal swipes and only if the event is cancelable
      if (horizontal && preventDefaultTouchmove && e.cancelable) {
        e.preventDefault();
      }
    }
    
    if (isHorizontalSwipe || Math.abs(deltaX) > Math.abs(deltaY)) {
      setCurrentX(touch.clientX);
      setCurrentY(touch.clientY);
      
      // Calculate and update velocity with smoothing
      if (currentTime - lastUpdateTime.current > 16) { // ~60fps throttling
        const velocity = calculateVelocity(currentTime, touch.clientX, startX, startTime);
        setSwipeVelocity(velocity);
        lastUpdateTime.current = currentTime;
      }
      
      // Only prevent default if the event is cancelable
      if (preventDefaultTouchmove && e.cancelable) {
        e.preventDefault();
      }
    }
  }, [isDragging, startX, startY, isHorizontalSwipe, preventDefaultTouchmove, touchStartDelay, disabled, calculateVelocity, startTime]);

  const handleTouchEnd = useCallback(() => {
    if (touchStartTimer.current) {
      clearTimeout(touchStartTimer.current);
    }
    
    if (!isDragging || disabled) {
      // Call onSwipeEnd even if disabled to clean up state
      if (onSwipeEnd) {
        onSwipeEnd();
      }
      return;
    }
    
    const deltaX = currentX - startX;
    const deltaY = currentY - startY;
    const deltaTime = Date.now() - startTime;
    const finalVelocity = swipeVelocity * dampingFactor; // Apply damping
    
    // Only process horizontal swipes
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Enhanced threshold logic with velocity consideration
      const effectiveThreshold = Math.max(threshold * 0.5, threshold - (finalVelocity * 100));
      
      if (Math.abs(deltaX) > effectiveThreshold || finalVelocity > velocityThreshold) {
        if (deltaX > 0) {
          onSwipeRight?.(Math.abs(deltaX), finalVelocity);
        } else {
          onSwipeLeft?.(Math.abs(deltaX), finalVelocity);
        }
      }
    }
    
    // Reset state
    setIsDragging(false);
    setStartX(0);
    setStartY(0);
    setCurrentX(0);
    setCurrentY(0);
    setStartTime(0);
    setIsHorizontalSwipe(false);
    setSwipeVelocity(0);
    velocityHistory.current = [];
    
    // Call onSwipeEnd callback
    if (onSwipeEnd) {
      onSwipeEnd();
    }
  }, [isDragging, currentX, startX, currentY, startY, startTime, threshold, velocityThreshold, onSwipeLeft, onSwipeRight, disabled, onSwipeEnd, swipeVelocity, dampingFactor]);

  const swipeOffset = isDragging ? currentX - startX : 0;

  // NEW: Reset function with smooth return animation
  const reset = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    setIsDragging(false);
    setStartX(0);
    setStartY(0);
    setCurrentX(0);
    setCurrentY(0);
    setIsHorizontalSwipe(false);
    setSwipeVelocity(0);
    velocityHistory.current = [];
    
    // Call onSwipeEnd on reset
    if (onSwipeEnd) {
      onSwipeEnd();
    }
  }, [onSwipeEnd]);

  return {
    // Touch event handlers
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    
    // Swipe state
    isDragging,
    swipeOffset: getResistantOffset(swipeOffset),
    rawSwipeOffset: swipeOffset,
    isHorizontalSwipe,
    
    // Enhanced gesture properties
    swipeDirection: swipeOffset > 0 ? 'right' : 'left',
    swipeProgress: Math.abs(swipeOffset) / threshold,
    velocity: swipeVelocity,
    
    // Helper methods
    reset,
    
    // NEW: Advanced state information
    isResistant: Math.abs(swipeOffset) > 200,
    resistanceLevel: Math.min(1, Math.abs(swipeOffset) / 300),
    shouldTrigger: Math.abs(swipeOffset) > threshold || swipeVelocity > velocityThreshold
  };
}; 